import styled from '@emotion/styled';
import { yupResolver } from '@hookform/resolvers/yup';
import {
    Button,
    Form,
    FormInputPassword,
    HeadingText40,
    PasswordValidationElement,
    pxToRem,
    useForm,
} from '@modernhealth/vital-components';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps } from 'react-router-dom';
import { navigationService, tokenAuthenticator, userAuthenticator } from 'src/config/rootService';
import { ValidateTokenData } from 'src/modules/auth/models/auth';
import {
    containsLowercaseLetter,
    containsNumber,
    containsSpecialCharacter,
    containsUppercaseLetter,
} from 'src/shared/helpers/characterContainValidators';
import { AuthLayout } from 'src/shared/layout/AuthLayout';
import * as Yup from 'yup';

interface FormData {
    password: string;
    passwordConfirm: string;
}

const ValidationContainer = styled.div({
    display: 'flex',
    flexWrap: 'wrap',
    paddingBottom: pxToRem(16),
    gridGap: pxToRem(12),
    width: '100%',
});

const PasswordResetForm: React.FC<RouteComponentProps<ValidateTokenData>> = ({
    match,
    location,
}) => {
    const { t } = useTranslation();
    const formSchema = Yup.object().shape({
        password: Yup.string()
            .test(
                'password-is-valid',
                '',
                (value: string) =>
                    containsSpecialCharacter(value) &&
                    containsUppercaseLetter(value) &&
                    containsLowercaseLetter(value) &&
                    containsNumber(value) &&
                    value.length >= 12,
            )
            .test(
                'password-not-matching-passwordConfirm',
                t('auth:password_reset_form.errors.passwords_do_not_match'),
                (value, context) => context.parent.passwordConfirm === value,
            ),
        passwordConfirm: Yup.string().test(
            'passwordConfirm-not-matching-password',
            t('auth:password_reset_form.errors.passwords_do_not_match'),
            (value, context) => context.parent.password === value,
        ),
    });
    const FormDefinition = useForm<FormData>({
        resolver: yupResolver(formSchema),
    });
    const password = FormDefinition.watch('password', '');
    const [isTokenValid, setIsTokenValid] = useState(false);
    const [wasPasswordEdited, setWasPasswordEdited] = useState(false);

    const getTokenData = (): ValidateTokenData => {
        const searchParams = new URLSearchParams(location.search);

        if (searchParams && searchParams.has('uidb64') && searchParams.has('token')) {
            return {
                uidb64: searchParams.get('uidb64') as string,
                token: searchParams.get('token') as string,
            };
        }

        return match.params;
    };

    const handleSubmit = async (data: FormData): Promise<void> => {
        await userAuthenticator.setNewPassword({
            password: data.password,
            ...getTokenData(),
        });

        const searchParams = new URLSearchParams(location.search);
        if (searchParams && searchParams.get('role') === 'COACH') {
            navigationService.navigate('/provider-dashboard');
        } else {
            navigationService.navigate('/login');
        }
    };

    useEffect(() => {
        tokenAuthenticator
            .verifyPasswordResetToken(getTokenData())
            .then(() => setIsTokenValid(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        setWasPasswordEdited(FormDefinition.formState.isDirty);
    }, [FormDefinition.formState.isDirty]);

    return (
        <AuthLayout>
            <HeadingText40 fontColor="blue75" css={{ marginBottom: pxToRem(32) }}>
                {t('auth:password_reset_form.password_reset')}
            </HeadingText40>
            {isTokenValid ? (
                <Form onSubmit={handleSubmit} definition={FormDefinition}>
                    <FormInputPassword
                        trim
                        name="password"
                        label={t('auth:password_reset_form.labels.new_password')}
                        dependentFields="passwordConfirm"
                        autoComplete="new-password"
                    />
                    <ValidationContainer>
                        <PasswordValidationElement
                            wasPasswordEdited={wasPasswordEdited}
                            isValid={password.trim().length >= 12}
                            text={t('auth:password_form_page.requirements.min_characters')}
                        />
                        <PasswordValidationElement
                            wasPasswordEdited={wasPasswordEdited}
                            isValid={containsSpecialCharacter(password)}
                            text={t(
                                'auth:password_form_page.password.requirements.min_especial_characters',
                            )}
                        />
                        <PasswordValidationElement
                            wasPasswordEdited={wasPasswordEdited}
                            isValid={containsNumber(password)}
                            text={t(
                                'auth:password_form_page.password.requirements.min_numeric_characters',
                            )}
                        />
                        <PasswordValidationElement
                            wasPasswordEdited={wasPasswordEdited}
                            isValid={containsUppercaseLetter(password)}
                            text={t(
                                'auth:password_form_page.password.requirements.min_uppercase_characters',
                            )}
                        />
                        <PasswordValidationElement
                            wasPasswordEdited={wasPasswordEdited}
                            isValid={containsLowercaseLetter(password)}
                            text={t(
                                'auth:password_form_page.password.requirements.min_lowercase_characters',
                            )}
                        />
                    </ValidationContainer>
                    <FormInputPassword
                        trim
                        name="passwordConfirm"
                        label={t('auth:password_reset_form.labels.confirm_new_password')}
                        dependentFields="password"
                        autoComplete="new-password"
                    />
                    <Button
                        type="submit"
                        size="large"
                        disabled={!FormDefinition.formState.isValid}
                        busy={FormDefinition.formState.isSubmitting}
                    >
                        {t('auth:password_reset_form.send')}
                    </Button>
                </Form>
            ) : null}
        </AuthLayout>
    );
};

export default PasswordResetForm;
